import { api, modelApi } from './axiosInterceptors';

// ════════════════════════════════════════════════════════════════════════════════
//  AUTHENTIFICATION
//  Gère la session utilisateur : connexion, déconnexion, profil courant
// ════════════════════════════════════════════════════════════════════════════════

const authService = {

  login: async (credentials) => {
    const response = await api.post('/auth/dashboard/login/', credentials);

    const token = response.headers['x-access-token'];
    if (!token) throw new Error('Réponse invalide du serveur: pas de token');

    const role = response.data?.data?.role;
    if (role === 'user') {
      throw new Error('Accès refusé.');
    }

    return {
      token,
      user: {
        id:       response.data?.data?.user_id,
        role:     response.data?.data?.role,
        username: response.data?.data?.username,
      },
    };
  },

  /** logout — Invalide la session côté serveur */
  logout: async () => {
    await api.post('/auth/logout/');
  },

  /** getCurrentUser — Vérifie et récupère la session active (utilisé au refresh de page) */
  getCurrentUser: async () => {
    const response = await api.get('/auth/me/');
    return response.data;
  },


  // ════════════════════════════════════════════════════════════════════════════
  //  PROFIL UTILISATEUR CONNECTÉ (moi)
  //  Endpoints relatifs à l'utilisateur actuellement connecté.
  // ════════════════════════════════════════════════════════════════════════════

  getMyProfile: async () => {
    const response = await api.get('/auth/me/');
    return response.data.data;
  },

  updateMyProfile: async (profileData) => {
    const response = await api.put('/auth/me/update/', profileData);
    return response.data;
  },

  updateMyPassword: async (passwordData) => {
    const response = await api.put('/auth/me/password/update/', passwordData);
    return response.data;
  },

  getMyStats: async () => {
    const response = await api.get('/activity/me/stats');
    return response.data.data;
  },

  getMyRecentActivity: async () => {
    const response = await api.get('/activity/me/recent');
    return response.data.data.activities;
  },


  // ════════════════════════════════════════════════════════════════════════════
  //  GESTION DES UTILISATEURS
  // ════════════════════════════════════════════════════════════════════════════

  getUsers: async () => {
    const response = await api.get('/auth/users/list');
    return response.data.data.users;
  },
  getUsers: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.is_online !== undefined && params.is_online !== null && params.is_online !== 'all') {
      query.append('is_online', params.is_online); // 'true' | 'false'
    }
    if (params.is_internal !== undefined && params.is_internal !== null && params.is_internal !== 'all') {
      query.append('is_internal', params.is_internal); // 'true' | 'false'
    }
    const qs = query.toString();
    const response = await api.get(`/auth/users/list${qs ? `?${qs}` : ''}`);
    return response.data.data.users;
  },

  getUser: async (userId) => {
    const response = await api.get(`/auth/users/${userId}/`);
    return response.data;
  },

  createAdmin: async (adminData, supervisorIds = []) => {
    const payload = {
      ...adminData,
      ...(supervisorIds.length > 0 && {
        supervisor_ids: supervisorIds.map(Number),
      }),
    };
    const response = await api.post('/auth/admin/create-admin', payload);
    return response.data;
  },

createUser: async (userData, adminId = null) => {
  const url = adminId
    ? `/auth/admin/create-user?supervised_by_admin_id=${adminId}`
    : '/auth/admin/create-user';
  const response = await api.post(url, userData);
  return response.data;
},
 
confirmCreateEmail: async (userId, type = 'user', adminId = null) => {
  const url = type === 'admin'
    ? `/auth/admin/create-admin?confirm=true&confirm_user_id=${userId}`
    : adminId
      ? `/auth/admin/create-user?confirm=true&confirm_user_id=${userId}&supervised_by_admin_id=${adminId}`
      : `/auth/admin/create-user?confirm=true&confirm_user_id=${userId}`;
  const response = await api.post(url, {});  // body vide
  return response.data;
},

  updateUser: async (userId, userData, confirm = false) => {
    const response = await api.put(
      `/auth/users/${userId}/`,
      userData,
      confirm ? { params: { confirm: true } } : {}
    );
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/auth/users/${userId}/`);
    return response.data;
  },
  
  confirmDeleteEmail: async (userId) => {
      const response = await api.delete(`/auth/users/${userId}/?confirm=true`);
      return response.data;
  },

  updateUserStatus: async (userId, active) => {
    const response = await api.patch(
        `/auth/users/${userId}/status`,
        null,
        { params: { active } }
    );
    return response.data; // ← retourner data complet pas data.data
},
 
confirmToggleEmail: async (userId) => {
    const response = await api.patch(
        `/auth/users/${userId}/status?confirm=true`,
        null,
        { params: { active: true } }
    );
    return response.data;
},

  // ════════════════════════════════════════════════════════════════════════════
  //  GESTION DE LA HIÉRARCHIE ADMIN
  // ════════════════════════════════════════════════════════════════════════════

  getAdmins: async () => {
    const response = await api.get('/auth/users/list?role=admin');
    return response.data.data.users;
  },

  getSupervisedAdmins: async () => {
    const response = await api.get('/auth/admin/supervised-admins');
    return response.data.data.admins;
  },

  assignSupervisor: async (userId, supervisorIdOrIds) => {
      if (Array.isArray(supervisorIdOrIds)) {
          const response = await api.put(
              `/auth/admin/${userId}/assign-supervisor`,
              { supervisor_ids: supervisorIdOrIds }
          );
          return response.data;
      }
      // admin → query param
      const response = await api.put(
          `/auth/admin/${userId}/assign-supervisor?supervisor_id=${supervisorIdOrIds}`
      );
      return response.data;
  },
 

  unassignSupervisor: async (adminId, supervisorId) => {
    const response = await api.put(
      `/auth/admin/${adminId}/unassign-supervisor?supervisor_id=${supervisorId}`
    );
    return response.data;
  },

  getAvailableSupervisors: async (targetId) => {
    const response = await api.get(`/auth/admin/${targetId}/available-supervisors`);
    return response.data.data.users;
  },

  getAvailableSupervisees: async (adminId) => {
    const response = await api.get(`/auth/admin/${adminId}/available-supervisees`);
    return response.data.data.users;
  },

  // ════════════════════════════════════════════════════════════════════════════
  //  ACTIVITÉ & LOGS
  // ════════════════════════════════════════════════════════════════════════════

  getGlobalOverview: async (microservice = null, startDate = null, endDate = null, adminId = null, isInternal = null) => {
    const params = {};
    if (microservice)        params.microservice    = microservice;
    if (startDate)           params.start_date      = startDate;
    if (endDate)             params.end_date        = endDate;
    if (adminId)             params.filter_admin_id = adminId;
    if (isInternal !== null) params.is_internal     = isInternal;

    const response = await api.get('/activity/admin/global-overview', { params });
    return response.data.data;
  },
getEndpointHistory: async (microservice, endpoint = null, startDate = null, endDate = null, adminId = null, isInternal = null) => {
    const params = {};
    if (microservice)        params.microservice    = microservice;
    if (endpoint)            params.service_name    = endpoint;   // ← corrigé
    if (startDate)           params.start_date      = startDate;
    if (endDate)             params.end_date        = endDate;
    if (adminId)             params.filter_admin_id = adminId;
    if (isInternal !== null) params.is_internal     = isInternal;
  
    const response = await api.get('/activity/admin/endpoint-history', { params });
    return response.data.data;
},

  getUserDetailed: async (userId, microservice = null, startDate = null, endDate = null, adminId = null, isInternal = null) => {
    const params = {};
    if (microservice)        params.microservice    = microservice;
    if (startDate)           params.start_date      = startDate;
    if (endDate)             params.end_date        = endDate;
    if (adminId)             params.filter_admin_id = adminId;
    if (isInternal !== null) params.is_internal     = isInternal;

    const response = await api.get(`/activity/admin/user/${userId}/detailed`, { params });
    return response.data.data;
  },

  getLiveLogsSnapshot: async (limit = 100, microservice = null, adminId = null) => {
    const params = { limit };
    if (microservice) params.microservice    = microservice;
    if (adminId)      params.filter_admin_id = adminId;

    const response = await api.get('/activity/admin/live/logs', { params });
    return response.data.data;
  },


  // ════════════════════════════════════════════════════════════════════════════
  //  BULK EMAIL
  // ════════════════════════════════════════════════════════════════════════════

  bulkEmail: async (payload) => {
    const isFormData = payload instanceof FormData;
    const response = await api.post('/auth/admin/bulk-email', payload, {
      headers: isFormData
        ? { 'Content-Type': 'multipart/form-data' }
        : { 'Content-Type': 'application/json' },
    });
    return response.data;
  },


  // ════════════════════════════════════════════════════════════════════════════
  //  NOTIFICATIONS
  // ════════════════════════════════════════════════════════════════════════════

  getNotifications: async (limit = 50, unreadOnly = false) => {
    const params = { limit };
    if (unreadOnly) params.unread_only = true;

    const response = await api.get('/notifications/', { params });
    return response.data.data;
  },

  markNotificationRead: async (notifId) => {
    const response = await api.put(`/notifications/${notifId}/read`);
    return response.data;
  },

  markAllNotificationsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  deleteNotification: async (notifId) => {
    const response = await api.delete(`/notifications/${notifId}`);
    return response.data;
  },


  // ════════════════════════════════════════════════════════════════════════════
  //  DROITS D'ACCÈS UTILISATEUR
  // ════════════════════════════════════════════════════════════════════════════

  getUserRights: async (userId) => {
    const response = await api.get(`/user-rights/${userId}`);
    return response.data.data;
  },

  updateUserRights: async (userId, rights) => {
    const response = await api.put(`/user-rights/${userId}`, { rights });
    return response.data;
  },

  revokeUserRights: async (userId) => {
    const response = await api.delete(`/user-rights/admin/${userId}/revoke`, { data: {} });
    return response.data;
  },

  confirmRightsEmail: async (userId) => {
      const response = await api.put(
          `/user-rights/${userId}?confirm=true`,
          { rights: [] }
      );
      return response.data;
  },

  // ════════════════════════════════════════════════════════════════════════════
  //  SIMULATIONS — microservice model-physic
  // ════════════════════════════════════════════════════════════════════════════
getFiresByUser: async (userId, filters = {}) => {
  const params = new URLSearchParams({ user_id: userId });
  if (filters.reference) params.set('reference', filters.reference);
  if (filters.dateFrom)  params.set('date_from', filters.dateFrom);
  if (filters.dateTo)    params.set('date_to', filters.dateTo);

  try {
    const response = await modelApi.get(`/v2/prediction/fires_by_user/?${params.toString()}`);
    const raw = response.data?.data ?? {};
    return Object.values(raw);
  } catch (err) {
    // Le backend renvoie 404 quand l'utilisateur n'a aucune fire — ce n'est
    // pas une erreur, juste une liste vide. Toute autre erreur remonte.
    if (err.response?.status === 404) return [];
    throw err;
  }
},

getMyFires: async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.reference) params.set('reference', filters.reference);
  if (filters.dateFrom)  params.set('date_from', filters.dateFrom);
  if (filters.dateTo)    params.set('date_to', filters.dateTo);
  const query = params.toString() ? `?${params.toString()}` : '';

  try {
    const response = await modelApi.get(`/v2/prediction/get_myfires/${query}`);
    const raw = response.data?.data ?? {};
    return Object.values(raw);
  } catch (err) {
    if (err.response?.status === 404) return [];
    throw err;
  }
},

  getFirePolygons: async (fireId) => {
    const response = await modelApi.get(
      `/v2/prediction/fire_polygons/?fire_id=${fireId}`
    );
    console.log(`authService.getFirePolygons(${fireId}) → ${response.data?.polygons?.length ?? 0} polygones`);
    return response.data?.polygons ?? [];
  },

 
getFiresByUsers: async (userIds, filters = {}) => {
  const params = new URLSearchParams({ user_ids: userIds.join(',') });
  if (filters.reference) params.set('reference', filters.reference);
  if (filters.dateFrom)  params.set('date_from', filters.dateFrom);
  if (filters.dateTo)    params.set('date_to', filters.dateTo);

  const response = await modelApi.get(`/v2/prediction/fires_by_users/?${params.toString()}`);
  const raw = response.data?.data ?? {}; // { "3": [...], "17": [...], ... }
  return raw;
},

};

export default authService;